import { useState, useEffect, useCallback } from 'react';
import useApi from '../useApi';
import axiosInstance from '../axiosInstance';

const BASE = '/holidays';

// ─── Plain API (exported for use outside hooks) ─────────────────────────────

export const getHolidayTypes = (params = {}) =>
  axiosInstance.get(BASE, { params }).then((res) => res.data);

export const getHolidayTypeById = (id, params = {}) =>
  axiosInstance.get(`${BASE}/${id}`, { params }).then((res) => res.data);

export const createHolidayType = (data) =>
  axiosInstance.post(BASE, data).then((res) => res.data);

export const updateHolidayType = (id, data) =>
  axiosInstance.put(`${BASE}/${id}`, data).then((res) => res.data);

export const deleteHolidayType = (id) =>
  axiosInstance.delete(`${BASE}/${id}`).then((res) => res.data);

export const hardDeleteHolidayType = (id) =>
  axiosInstance.delete(`${BASE}/${id}/permanent`).then((res) => res.data);

export const assignHolidayType = (id, entityType, entityId) =>
  axiosInstance
    .post(`${BASE}/${id}/assign/${entityType}/${entityId}`)
    .then((res) => res.data);

export const unassignHolidayType = (id, entityType, entityId) =>
  axiosInstance
    .delete(`${BASE}/${id}/unassign/${entityType}/${entityId}`)
    .then((res) => res.data);

export const getHolidayTypesByEntity = (entityType, entityId, params = {}) =>
  axiosInstance
    .get(`${BASE}/by-entity/${entityType}/${entityId}`, { params })
    .then((res) => res.data);

/**
 * Get holiday types assigned to a country
 */
export const getCountryHolidayTypes = (countryId, params = {}) =>
  axiosInstance
    .get(`${BASE}/countries/${countryId}/holiday-types`, { params })
    .then((res) => res.data);

/**
 * Assign holiday types to a country (replaces existing assignments)
 * @param {number|string} countryId
 * @param {number[]} holidayTypeIds - array of holiday type IDs
 */
export const assignHolidayTypesToCountry = (countryId, holidayTypeIds) =>
  axiosInstance
    .post(`${BASE}/countries/${countryId}/holiday-types`, { holidayTypeIds })
    .then((res) => res.data);

/** Region holiday types (get + replace-all assign) */
export const getRegionHolidayTypes = (regionId, params = {}) =>
  axiosInstance
    .get(`${BASE}/regions/${regionId}/holiday-types`, { params })
    .then((res) => res.data);

export const assignHolidayTypesToRegion = (regionId, holidayTypeIds) =>
  axiosInstance
    .post(`${BASE}/regions/${regionId}/holiday-types`, { holidayTypeIds })
    .then((res) => res.data);

/** City holiday types (get + replace-all assign) */
export const getCityHolidayTypes = (cityId, params = {}) =>
  axiosInstance
    .get(`${BASE}/cities/${cityId}/holiday-types`, { params })
    .then((res) => res.data);

export const assignHolidayTypesToCity = (cityId, holidayTypeIds) =>
  axiosInstance
    .post(`${BASE}/cities/${cityId}/holiday-types`, { holidayTypeIds })
    .then((res) => res.data);

// ─── Hooks ──────────────────────────────────────────────────────────────────

/**
 * useGetHolidayTypes - fetch all holiday/theme types with optional filters and pagination
 * @param {Object} params - { category, active, appliesTo, languageCode, page, limit }
 *   - category: 'Holiday' | 'Theme'
 *   - active: true | false
 *   - appliesTo: 'Country' | 'Region' | 'City' | 'Zone' | 'Hotel'
 *   - languageCode: e.g. 'ENG', 'FRE'
 *   - page: page number (default 1)
 *   - limit: page size (default 10)
 * @returns { data, count, pagination, loading, error, refetch }
 */
export const useGetHolidayTypes = (params = {}) => {
  const {
    category = '',
    active = '',
    appliesTo = '',
    languageCode = 'ENG',
    page = 1,
    limit = 10,
  } = params;

  const [data, setData] = useState([]);
  const [count, setCount] = useState(0);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTypes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const queryParams = { languageCode, page, limit };
      if (category) queryParams.category = category;
      if (active !== '') queryParams.active = active;
      if (appliesTo) queryParams.appliesTo = appliesTo;

      const response = await axiosInstance.get(BASE, { params: queryParams });
      const res = response.data;

      if (res.success) {
        const raw = res.data;
        const list = Array.isArray(raw)
          ? raw
          : raw && typeof raw === 'object' && raw !== null
            ? [raw]
            : [];
        setData(list);
        setCount(res.count ?? list.length);
        if (res.pagination) {
          setPagination(res.pagination);
        }
      } else {
        setError(res.message || 'Failed to load holiday/theme types');
        setData([]);
        setCount(0);
      }
    } catch (err) {
      if (err.response) {
        setError(
          err.response?.data?.message ||
            err.response?.data?.error ||
            `Request failed (${err.response.status})`
        );
      } else if (err.request) {
        setError(
          'Cannot reach server. Check that the backend is running (e.g. http://localhost:5000).'
        );
      } else {
        setError(err.message || 'Failed to load holiday/theme types');
      }
      setData([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [category, active, appliesTo, languageCode, page, limit]);

  useEffect(() => {
    fetchTypes();
  }, [fetchTypes]);

  const refetch = useCallback(() => {
    fetchTypes();
  }, [fetchTypes]);

  return { data, count, pagination, loading, error, refetch };
};

/**
 * useGetHolidayType - fetch single holiday/theme type by id
 * @param {number|string} id - type id
 * @param {Object} options - { languageCode }
 * @returns { data, loading, error, refetch }
 */
export const useGetHolidayType = (id, options = {}) => {
  const { languageCode = 'ENG' } = options;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!!id);
  const [error, setError] = useState(null);

  const fetchType = useCallback(async () => {
    if (!id) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(`${BASE}/${id}`, {
        params: { languageCode },
      });
      const res = response.data;

      if (res.success) {
        setData(res.data);
      } else {
        setError(res.message || 'Failed to load holiday/theme type');
        setData(null);
      }
    } catch (err) {
      if (err.response) {
        setError(
          err.response?.data?.message ||
            err.response?.data?.error ||
            `Request failed (${err.response.status})`
        );
      } else {
        setError(err.message || 'Failed to load holiday/theme type');
      }
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [id, languageCode]);

  useEffect(() => {
    fetchType();
  }, [fetchType]);

  const refetch = useCallback(() => {
    fetchType();
  }, [fetchType]);

  return { data, loading, error, refetch };
};

/**
 * useCreateHolidayType - create holiday/theme type (useApi pattern)
 */
export const useCreateHolidayType = () => {
  return useApi(BASE, { method: 'POST' });
};

/**
 * useUpdateHolidayType - update holiday/theme type by id
 * @returns { execute(id, payload), loading, error }
 */
export const useUpdateHolidayType = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id, payload) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.put(`${BASE}/${id}`, payload);
      return response.data;
    } catch (err) {
      const msg =
        err.response?.data?.message || 'Failed to update holiday/theme type';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
};

/**
 * useDeleteHolidayType - soft delete (sets deletedAt) - for toggling/deactivating
 */
export const useDeleteHolidayType = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.delete(`${BASE}/${id}`);
      return response.data;
    } catch (err) {
      const msg =
        err.response?.data?.message || 'Failed to delete holiday/theme type';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
};

/**
 * useHardDeleteHolidayType - permanently delete holiday/theme type from DB
 */
export const useHardDeleteHolidayType = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.delete(`${BASE}/${id}/permanent`);
      return response.data;
    } catch (err) {
      const msg =
        err.response?.data?.message || 'Failed to permanently delete holiday/theme type';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
};

/**
 * useAssignHolidayType - assign holiday/theme type to an entity
 * @returns { execute(id, entityType, entityId), loading, error }
 * entityType: 'country' | 'region' | 'city' | 'zone' | 'hotel'
 */
export const useAssignHolidayType = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id, entityType, entityId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.post(
        `${BASE}/${id}/assign/${entityType}/${entityId}`
      );
      return response.data;
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        'Failed to assign holiday/theme type to entity';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
};

/**
 * useUnassignHolidayType - remove holiday/theme type from an entity
 * @returns { execute(id, entityType, entityId), loading, error }
 */
export const useUnassignHolidayType = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id, entityType, entityId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.delete(
        `${BASE}/${id}/unassign/${entityType}/${entityId}`
      );
      return response.data;
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        'Failed to remove holiday/theme type from entity';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
};

/**
 * useGetHolidayTypesByEntity - fetch holiday/theme types assigned to an entity
 * @param {string} entityType - 'country' | 'region' | 'city' | 'zone' | 'hotel'
 * @param {number|string} entityId - entity id
 * @param {Object} params - { languageCode }
 * @returns { data, count, loading, error, refetch }
 */
export const useGetHolidayTypesByEntity = (
  entityType,
  entityId,
  params = {}
) => {
  const { languageCode = 'ENG' } = params;

  const [data, setData] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(!!(entityType && entityId));
  const [error, setError] = useState(null);

  const fetchByEntity = useCallback(async () => {
    if (!entityType || entityId == null || entityId === '') {
      setData([]);
      setCount(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(
        `${BASE}/by-entity/${entityType}/${entityId}`,
        { params: { languageCode } }
      );
      const res = response.data;

      if (res.success) {
        setData(res.data || []);
        setCount(res.count ?? (res.data || []).length);
      } else {
        setError(res.message || 'Failed to load types for entity');
        setData([]);
        setCount(0);
      }
    } catch (err) {
      if (err.response) {
        setError(
          err.response?.data?.message ||
            err.response?.data?.error ||
            `Request failed (${err.response.status})`
        );
      } else {
        setError(err.message || 'Failed to load types for entity');
      }
      setData([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId, languageCode]);

  useEffect(() => {
    fetchByEntity();
  }, [fetchByEntity]);

  const refetch = useCallback(() => {
    fetchByEntity();
  }, [fetchByEntity]);

  return { data, count, loading, error, refetch };
};
